import { useState, useEffect, useRef } from 'react';
import { useGameStore } from '../../store/gameStore';

export function TimerDisplay({
  expiresAt,
  variant = 'text',
  className = ''
}: {
  expiresAt: number,
  variant?: 'text' | 'bar' | 'giant',
  className?: string
}) {
  const [timeLeft, setTimeLeft] = useState(() => {
    const offset = useGameStore.getState().clockOffset;
    return Math.max(0, expiresAt - (Date.now() + offset));
  });
  const [totalTime, setTotalTime] = useState(15000);
  const lastExpiresAt = useRef(expiresAt);

  useEffect(() => {
    // If the timer is reset to a new future time, recalculate the total duration
    const offset = useGameStore.getState().clockOffset;
    const diff = expiresAt - (Date.now() + offset);

    // Only reset totalTime if this is a fresh timer (expiresAt changed significantly)
    if (Math.abs(expiresAt - lastExpiresAt.current) > 2000 || diff > totalTime) {
      if (diff > 25000) setTotalTime(30000);
      else if (diff > 12000) setTotalTime(15000);
      else if (diff > 7000) setTotalTime(10000);
      else setTotalTime(5000);
      lastExpiresAt.current = expiresAt;
    }

    const interval = setInterval(() => {
      const off = useGameStore.getState().clockOffset;
      const remaining = Math.max(0, expiresAt - (Date.now() + off));
      setTimeLeft(remaining);
      if (remaining <= 0) clearInterval(interval);
    }, 16);

    return () => clearInterval(interval);
  }, [expiresAt]);

  const secs = Math.ceil(timeLeft / 1000);

  if (variant === 'bar') {
    const percentage = Math.max(0, Math.min(100, (timeLeft / totalTime) * 100));
    const isWarning = secs <= 5;
    const color = isWarning ? 'var(--color-danger)' : 'var(--color-primary)';

    return (
      <div className={`flex flex-col items-center gap-3 w-full max-w-4xl mx-auto px-4 ${className}`}>
        <div className="flex flex-col items-center">
          <span className="text-white/30 text-[10px] font-black uppercase tracking-[0.3em] mb-1">TIEMPO RESTANTE</span>
          <span className={`text-5xl font-black tabular-nums transition-all ${isWarning ? 'text-danger scale-110' : 'text-white'}`}
            style={{
              textShadow: isWarning ? '0 0 30px var(--color-danger)' : '0 0 20px rgba(255,255,255,0.3)',
              animation: isWarning ? 'pulse 1s infinite' : 'none'
            }}
          >
            {secs}
          </span>
        </div>
        <div
          className="h-6 w-full rounded-full bg-black/40 p-[3px] border border-white/10 backdrop-blur-sm shadow-[inset_0_2px_10px_rgba(0,0,0,0.5)]"
        >
          <div
            className={`h-full rounded-full transition-all duration-75 ease-linear`}
            style={{
              width: `${percentage}%`,
              backgroundColor: color,
              boxShadow: `0 0 20px ${color}88, inset 0 2px 4px rgba(255,255,255,0.3)`
            }}
          />
        </div>
      </div>
    );
  }

  if (variant === 'giant') {
    if (secs <= 0) return null; // hide when done, don't show 'YA'
    return (
      <div
        key={secs}
        className={`text-[15rem] md:text-[24rem] font-black leading-none drop-shadow-[0_0_60px_var(--color-primary)] ${secs <= 1 ? 'text-danger' : 'text-primary'} ${className}`}
        style={{ animation: 'numberPop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) forwards' }}
      >
        {secs}
      </div>
    );
  }

  // Text variant processing
  if (secs <= 0) {
    return <div className={`animate-pulse ${className}`}>0s</div>;
  }
  if (secs <= 5) {
    return <div className={`text-danger animate-pulse ${className}`}>{secs}s</div>;
  }
  return <div className={className}>{secs}s</div>;
}
